import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import useSpeech from '../../hooks/useSpeech';
import { extractForeign } from '../../utils/foreignText';
import { profileSelectors } from '../../store/selectors/profile';
import './speak-button.scss';

const NO_FILL = { fill: 'none' };

/** Повторний клік раніше за цей час після старту — випадковий дабл-клік, озвучку не зупиняємо. */
const STOP_GUARD_MS = 700;
/** Скільки висить підказка «звук не вийшов». */
const HINT_MS = 6000;

const HINT_NO_VOICE = 'У цьому браузері немає голосу для цієї мови. Відкрийте сторінку в Chrome або Safari.';
const HINT_FAILED = 'Браузер не відтворив звук. Перевірте гучність або відкрийте сторінку в Chrome чи Safari.';

/**
 * Кнопки озвучки в домашках і на сторінці спроб з'являються всім разом зі сторінкою
 * «Слова з озвучкою» — 2 жовтня 2026, 12:00 за Києвом (той самий час, що
 * `vocabulary.preview.until` у cabinet-backend). До цього моменту не показуються.
 * Сторінку словника це не зачіпає (вона бере лише SpeakerIcon).
 */
const SPEAK_BUTTONS_OPEN_AT = Date.parse('2026-10-02T12:00:00+03:00');

/** Кабінети, яким кнопки показуються ще до відкриття — для тестування. */
const EARLY_ACCESS_USER_IDS = [3];

/**
 * Id студента: профіль у сторі є лише одразу після входу, після оновлення сторінки
 * він порожній — тоді беремо збережений при вході `user` з localStorage.
 */
const readSavedUserId = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null')?.id;
  } catch (e) {
    return undefined;
  }
};

/** Динамік із хвилями; поки звучить — три «стовпчики». */
export const SpeakerIcon = ({ speaking }) => (speaking ? (
  <span className="speak-btn__waves" aria-hidden="true"><i /><i /><i /></span>
) : (
  <svg className="speak-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 9.5v5h3.2L12 18.5v-13L7.2 9.5H4z" style={NO_FILL} />
    <path d="M15.5 9.2a4 4 0 0 1 0 5.6" style={NO_FILL} />
    <path d="M18.2 6.6a7.6 7.6 0 0 1 0 10.8" style={NO_FILL} />
  </svg>
));

SpeakerIcon.propTypes = { speaking: PropTypes.bool };
SpeakerIcon.defaultProps = { speaking: false };

/**
 * Кнопка озвучки іноземного фрагмента тексту (тести, підказки, завдання).
 *
 * Сама вирішує, чи є що читати: з `text` береться лише іноземна частина
 * (`extractForeign`), і якщо її нема — кнопка не рендериться взагалі.
 * Клік не спливає до батьківської картки, щоб не зарахувати відповідь.
 */
const SpeakButtonInner = ({ text, lang, size, className }) => {
  const phrase = extractForeign(text, lang);
  const speech = useSpeech(lang);
  const [hint, setHint] = useState('');
  const startedAtRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    if (!hint) return undefined;
    const timer = setTimeout(() => setHint(''), HINT_MS);

    return () => clearTimeout(timer);
  }, [hint]);

  if (!phrase || !speech.isSupported) return null;

  const speaking = speech.speakingId === phrase;

  const handleClick = async (e) => {
    e.stopPropagation();

    // Клік по самій підказці лише ховає її (і не проходить до картки відповіді під нею)
    if (e.target.closest?.('.speak-btn__hint')) {
      setHint('');

      return;
    }

    if (speaking) {
      if (Date.now() - startedAtRef.current >= STOP_GUARD_MS) speech.stop();

      return;
    }

    // Голоси пристрою відомі, але для мови курсу жодного: браузер прочитав би слово
    // чужим голосом або мовчки (так буває в Arc, Opera, Chromium без голосів Google)
    if (speech.hasVoiceList && !speech.hasVoice) {
      setHint(HINT_NO_VOICE);

      return;
    }

    setHint('');
    startedAtRef.current = Date.now();
    const ok = await speech.speakText(phrase);
    if (!ok && mountedRef.current) setHint(HINT_FAILED);
  };

  return (
    <button
      type="button"
      className={`speak-btn speak-btn--${size} ${speaking ? 'speak-btn--speaking' : ''} ${className}`.trim()}
      aria-label={`Озвучити: ${phrase}`}
      title="Озвучити"
      onClick={handleClick}
    >
      <SpeakerIcon speaking={speaking} />
      {hint && <span className="speak-btn__hint" role="status">{hint}</span>}
    </button>
  );
};

/** Обгортка-вимикач: до відкриття хук озвучки навіть не запускається (крім кабінетів для тестування). */
const SpeakButton = (props) => {
  const userId = Number(useSelector(profileSelectors)?.user?.id || readSavedUserId());
  const isOpen = Date.now() >= SPEAK_BUTTONS_OPEN_AT || EARLY_ACCESS_USER_IDS.includes(userId);

  return isOpen ? <SpeakButtonInner {...props} /> : null;
};

SpeakButtonInner.propTypes = {
  text: PropTypes.string,
  lang: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md']),
  className: PropTypes.string,
};

SpeakButtonInner.defaultProps = {
  text: '',
  lang: '',
  size: 'sm',
  className: '',
};

export default SpeakButton;
